/**
 * THE ECLIPSE STUDY, on the chart.
 *
 * <h2>Every mark sits on the RAIL, because every rule here is a DATE</h2>
 *
 * All nine rules answer "when" — an aspect became exact, a body reached a recorded degree, a
 * planet returned direct to its station longitude. Not one of them computes a price, a level or a
 * direction. So the marks carry NO `price` at all and `anchor: 'bottom'` puts them on the 0..1
 * rail at the foot of the grid, which says "this bar" and nothing more. That absence is not
 * tolerated by luck: `onRailFor` routes a point with no finite price to the rail BY RULE, so
 * omitting the price IS the instruction.
 *
 * <p>A triangle at a price would make the directional claim the CTS marks were moved off the
 * candles for making, and these have even less to say about direction than those did.
 *
 * <h2>⚠ ONE MARK PER BAR, carrying everything that happened on it</h2>
 *
 * Unlike the midpoint-trine scan, this study routinely puts several events on one day — a
 * conjunction to the eclipse degree and two harmonics off it is an ordinary Tuesday. Drawn one
 * mark per EVENT they would stack into an indistinguishable blob at identical coordinates, and a
 * reader hovering would get whichever one ECharts hit-tested first.
 *
 * <p>So the marks are grouped by bar and the tooltip lists every tag on that bar. The count rides
 * in the label, so a dense day is visible before the hover rather than after it.
 *
 * <h2>The ECLIPSE bar is a CIRCLE; every other bar is a tick</h2>
 *
 * Shape rather than colour, because shape survives a monochrome screenshot and a colour-blind
 * reader — the same reasoning that gives the dual ledger its solid/hollow silhouettes. A circle
 * for the eclipse is also what an eclipse looks like. One colour across both, because they are
 * one study and nine hues would be nine things to learn.
 *
 * <h2>A non-trading day has no column, and the sky does not keep market hours</h2>
 *
 * The x axis is a CATEGORY axis over the `YYYY-MM-DD` strings in `barData.dates`, so a Saturday is
 * not one of them and handing the renderer that date makes ECharts drop the point in silence —
 * roughly two in seven would vanish. They are SNAPPED FORWARD to the next trading day, as the
 * celestial and midpoint-trine marks are, and the tooltip says so on the card for that bar.
 */

/** One id, so a re-run replaces the previous paint rather than stacking on it. */
export const ECLIPSE_STUDY_OVERLAY_ID = 'eclipse-study-markers';

/**
 * A mid purple.
 *
 * <p>Chosen against the two canvases rather than by eye. The app defaults to DARK, and the obvious
 * deeper purples fail there outright: `#4A148C` measures **1.58:1** against `#0d1117` and
 * `#8E24AA` **2.69:1**, both under the 3:1 a graphical object needs to register at all. This one
 * is **3.93:1 on dark and 4.66:1 on light** — the narrow band that clears both, since going
 * lighter to help the dark canvas costs the light one.
 *
 * <p>Distinct from everything else that can share this chart: Deep Celestial indigo `#4C5B8A`, CTS
 * amber `#FFC107`, PTA `#F59E0B`, PTB `#06B6D4`, Deep Pivot bronze `#8B7355`, the yearly plaque's
 * gold `#D4A017` and the midpoint-trine teal `#00796B`.
 */
export const ECLIPSE_STUDY_COLOR = '#AB47BC';

/**
 * At most this many BARS carry a mark.
 *
 * <p>⚠ MEASURED, not inherited. It shipped at 300 — the figure the midpoint-trine scan uses, on
 * the reasoning that ECharts lays out every mark on every redraw — and on a 33-year SPY chart
 * that hid <b>1,419 of 1,719 bars, 83% of the study</b>. So the cost was measured on that chart,
 * minimum of three resizes each:
 *
 * <pre>
 *   0 marks    127 ms     1,719 marks  169 ms
 *   300 marks  129 ms     3,000 marks  132 ms
 *   900 marks  144 ms
 * </pre>
 *
 * <p>The ~130 ms is the 8,485-candle chart; across the whole range the marks are inside the
 * noise — 3,000 measures FASTER than 1,719. This path renders a scatter series rather than a
 * markPoint, which is the likely reason the inherited figure did not apply. 3,000 covers every
 * chart in this repository at full density (the longest, XAUUSD at 12,293 bars, yields ~2,400)
 * and still bounds a pathological run.
 *
 * <p>REPORTED by the caller rather than applied in silence — a truncated overlay reads as "this
 * is all of them", and under rule 2 the absence of a marker is itself a finding.
 */
export const ECLIPSE_STUDY_MAX_MARKS = 3000;

/**
 * The index of the first trading day on or after a date, or -1 past the end.
 *
 * <p>`dates` is sorted and in `YYYY-MM-DD`, so a string comparison IS a date comparison. Parsing
 * would reintroduce the timezone shift this repository has already been caught by, to answer a
 * question that needs no clock.
 */
export function indexAtOrAfter(dates, day) {
  if (!Array.isArray(dates) || dates.length === 0 || !day) return -1;
  let lo = 0;
  let hi = dates.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (dates[mid] >= day) {
      found = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return found;
}

/** The window's own header line, when a bar carries an eclipse. */
function eclipseLines(window) {
  const lines = [`Solar eclipse at ${window.eclipseLongitude}`];
  if (window.primary) {
    lines.push(`Eclipse planet: ${window.primary}`
      + (window.primaryAspect ? ` (${window.primaryAspect} to the Sun` : '')
      + (window.primaryAspect && Number.isFinite(window.primaryOrb)
        ? `, orb ${window.primaryOrb}°)` : window.primaryAspect ? ')' : ''));
    // The baseline rule is specifically about the body to the Sun's LEFT, so whether the pick
    // satisfied that or was the only aspecting body is the one thing a reader auditing the
    // selection needs. Reporting only the name hides which of the two it was.
    lines.push(window.primaryFollowsSun
      ? 'Follows the Sun in forward zodiacal order'
      : 'Does NOT follow the Sun — chosen as the only aspecting body');
  } else {
    lines.push('No body aspected the Sun at this eclipse');
  }
  if (Array.isArray(window.active) && window.active.length) {
    lines.push(`Active: ${window.active.join(', ')}`);
  }
  if (window.extended) lines.push('Window EXTENDED from the previous eclipse');
  if (window.open) lines.push('Window still open at the end of the range');
  return lines;
}

/**
 * Build the overlay.
 *
 * @param report the backend's `{windows, events}`
 * @param dates  the chart's own x-axis categories, `YYYY-MM-DD`, ascending
 * @returns `{overlay, bars, events, eclipses, snapped, beyondChart, truncated}` — `overlay: null`
 *          when nothing can be drawn, so the caller can distinguish "the study found nothing" from
 *          "none of it lands on this chart". Those are different findings.
 */
export function buildEclipseStudyOverlay(report, dates) {
  const windows = Array.isArray(report && report.windows) ? report.windows : [];
  const rows = Array.isArray(report && report.events) ? report.events : [];

  let snapped = 0;
  let beyondChart = 0;
  // Insertion-ordered, so the bars come out in the order they were first seen. The events arrive
  // chronologically from the engine, which makes that chart order too.
  const byBar = new Map();

  const place = (day) => {
    if (!day) return null;
    const idx = indexAtOrAfter(dates, day);
    if (idx === -1) {
      // Past the last bar. Dropped rather than clamped: clamping piles every future event onto
      // the final column, which reads as a cluster that is not there.
      beyondChart += 1;
      return null;
    }
    const barDate = dates[idx];
    if (barDate !== day) snapped += 1;
    let bucket = byBar.get(barDate);
    if (!bucket) {
      bucket = { date: barDate, tags: [], eclipse: null, snappedFrom: new Set() };
      byBar.set(barDate, bucket);
    }
    if (barDate !== day) bucket.snappedFrom.add(day);
    return bucket;
  };

  // The eclipses first, so a bar carrying one is a circle however its events sort.
  let eclipses = 0;
  windows.forEach((w) => {
    const bucket = place(w && w.eclipseDate);
    if (!bucket) return;
    eclipses += 1;
    bucket.eclipse = w;
  });

  let events = 0;
  rows.forEach((e) => {
    const bucket = place(e && e.date);
    if (!bucket) return;
    events += 1;
    bucket.tags.push(e);
  });

  // ⚠ SORTED BY DATE BEFORE THE CAP SLICES, and the eclipses are why.
  //
  // The eclipses are bucketed FIRST so an eclipse bar is a circle however its events sort, which
  // puts all of them at the head of the INSERTION order. Slicing the tail of that order drops
  // every single eclipse bar — on a 33-year SPY chart that was 74 circles lost and 300 ticks
  // kept, so the one mark that says "a window opens here" never appeared at all. Found by
  // counting the symbols on a live chart; the unit test could not see it, because its fixture had
  // no eclipse outside the kept slice.
  //
  // The same trap `recent()` in the Chart Analytica overlay already records: input order is bar
  // order only by accident of how the producer happened to walk its data.
  const buckets = Array.from(byBar.values()).sort((x, y) => (x.date < y.date ? -1 : 1));
  const truncated = Math.max(0, buckets.length - ECLIPSE_STUDY_MAX_MARKS);
  // The MOST RECENT, not the first found. A reader is looking at the right-hand end of a chart,
  // and keeping the oldest would empty exactly the stretch they are reading.
  const kept = truncated > 0 ? buckets.slice(buckets.length - ECLIPSE_STUDY_MAX_MARKS) : buckets;

  if (kept.length === 0) {
    return {
      overlay: null, bars: 0, events, eclipses, snapped, beyondChart, truncated: 0,
    };
  }

  const points = kept.map((bucket) => {
    const lines = [];
    if (bucket.eclipse) lines.push(...eclipseLines(bucket.eclipse));
    bucket.tags.forEach((e) => {
      // THE TAG LEADS, because the tag is the format the study was asked to report in and is what
      // a reader is scanning for. The description is already inside it.
      lines.push(e.tag);
      if (e.activeUntil) lines.push(`    active until ${e.activeUntil}`);
    });
    if (bucket.snappedFrom.size) {
      // Said explicitly. A mark on a bar whose date is not the event's date is the one thing here
      // a reader could reasonably take for a bug rather than a convention.
      lines.push(`Fell on ${Array.from(bucket.snappedFrom).sort().join(', ')}`
        + ' — a non-trading day, drawn on the next bar');
    }
    return {
      date: bucket.date,
      // NO price, deliberately — see the file note. This is what sends it to the rail.
      label: bucket.tags.length > 1 ? String(bucket.tags.length) : '',
      // Per-point SHAPE. The rail path reads `p.symbol`, and this is the channel that survives a
      // monochrome screenshot — which colour alone does not.
      symbol: bucket.eclipse ? 'circle' : 'rect',
      tooltipLines: lines,
      tooltipFooter: bucket.eclipse
        ? `Eclipse window opens ${bucket.eclipse.eclipseDate}`
        : `${bucket.tags.length} event${bucket.tags.length === 1 ? '' : 's'}`,
    };
  });

  return {
    overlay: {
      type: 'pivotMarkers',
      id: ECLIPSE_STUDY_OVERLAY_ID,
      points,
      anchor: 'bottom',
      color: ECLIPSE_STUDY_COLOR,
      colorRole: 'eclipseStudy',
      label: 'Eclipse Study',
      // The fallback for any point that does not name its own shape. Every one of them does, so
      // this is only ever reached if a point is added later without one.
      symbol: 'rect',
      symbolSize: 9,
      // ADMIN RESEARCH, so the short-bar preset does not swallow the hover card. That preset is a
      // customer-facing constraint — nothing astrological reaches the screen — and this study is
      // reachable only from a panel that renders nothing for a non-admin, so there is no customer
      // to constrain. Without it the marks draw and answer no hover, which reads as broken rather
      // than as restricted.
      researchTooltip: true,
      source: 'eclipseStudy',
    },
    bars: points.length,
    events,
    eclipses,
    snapped,
    beyondChart,
    truncated,
  };
}

/** The one-line status. Every number it quotes is a decision, so none of them is silent. */
export function describeEclipseStudy({
  eclipseCount, eventCount, bars, snapped, beyondChart, truncated, warning,
}) {
  if (warning) return `Eclipse Study: ${warning}`;
  if (!eclipseCount) return 'Eclipse Study: no solar eclipses in this range.';
  if (!eventCount) {
    return `Eclipse Study: ${eclipseCount} solar eclipse${eclipseCount === 1 ? '' : 's'}, `
      + 'no tracked events within the orb.';
  }
  const parts = [
    `Eclipse Study: ${eclipseCount} solar eclipse${eclipseCount === 1 ? '' : 's'}`,
    `${eventCount} event${eventCount === 1 ? '' : 's'}`,
    `${bars} bar${bars === 1 ? '' : 's'} marked`,
  ];
  if (snapped) parts.push(`${snapped} snapped forward from a non-trading day`);
  if (beyondChart) parts.push(`${beyondChart} past the last bar`);
  if (truncated) {
    parts.push(`${truncated} older bar${truncated === 1 ? '' : 's'} not drawn `
      + `(cap ${ECLIPSE_STUDY_MAX_MARKS})`);
  }
  return `${parts.join(' · ')}.`;
}

/**
 * How many of each marker a run produced.
 *
 * <p>Nine rules drawn in one colour means the chart cannot say which fired. The breakdown is how a
 * reader learns that, say, rule 7 is silent on this range — which under this study is a finding
 * rather than an absence.
 */
export function markerBreakdown(events) {
  const counts = new Map();
  (Array.isArray(events) ? events : []).forEach((e) => {
    const name = e && e.marker;
    if (!name) return;
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([marker, count]) => ({ marker, count }));
}
